
import type { LoaderFunctionArgs } from 'react-router-dom';
import { Form, redirect, useActionData, useLoaderData } from 'react-router-dom';
import { Button, Input } from 'semantic-ui-react';
import { gql } from '@apollo/client';

import { authProvider } from '../../auth';
import { PATHS } from '../../paths';
import { client } from '../../apolloClient';
import { AuthCard } from '../AuthCard';

import formStyles from '../AuthCard/form.module.css';

const SIGNUP_MUTATION = gql`
    mutation CreateUser($code: String!, $userName: String!, $password: String!) {
        createUser(code: $code, userName: $userName, password: $password) {
            token
            refreshToken
        }
    }
`;

/**
 * Both operation names are in the server's auth-bypass list — somebody following an invitation
 * link has no session yet, so there is no token to present.
 */
const INVITATION = gql`
    query Invitation($token: String!) {
        invitation(token: $token) {
            organisationName
            email
            name
            roleName
        }
    }
`;

const ACCEPT_INVITATION = gql`
    mutation AcceptInvitation($token: String!, $password: String!) {
        acceptInvitation(token: $token, password: $password) {
            success
            token
            refreshToken
        }
    }
`;

const ROLE_LABELS: Record<string, string> = {
    PARTNER_ADMIN: 'administrator',
    PARTNER_MEMBERSHIP_OFFICER: 'membership manager',
    PARTNER_LEADERSHIP: 'leadership',
};

interface InvitePrefill {
    token: string;
    organisationName: string;
    email: string;
    name: string | null;
    roleName: string;
}

function SignUpPage() {
    const actionData = useActionData() as { error: string } | undefined;
    const invite = useLoaderData() as InvitePrefill | null;

    // An invitation means the organisation, the address and the role are already settled — the
    // only thing missing is a password. Asking for the other three again would be asking somebody
    // to retype what the email already told them, with a chance of getting it wrong.
    if (invite) {
        const role = ROLE_LABELS[invite.roleName] ?? 'member';

        return (
            <AuthCard title={`Join ${invite.organisationName}`}>
                <p className={formStyles.switchLink} style={{ marginTop: 0 }}>
                    You've been invited as {invite.organisationName}'s {role}. Choose a password to
                    finish.
                </p>
                <Form method="post" replace>
                    <input type="hidden" name="invite" value={invite.token} />
                    <div className={formStyles.field}>
                        {/* Read-only: the address is what the invitation was sent to, and letting
                            it be edited here would create an account the invitation cannot match. */}
                        <Input fluid icon='mail' iconPosition='left' value={invite.email} readOnly />
                    </div>
                    <div className={formStyles.field}>
                        <Input
                            fluid
                            icon='lock'
                            iconPosition='left'
                            placeholder='Choose a password'
                            name='password'
                            type='password'
                        />
                    </div>
                    {actionData?.error && (
                        <p className={formStyles.errorMsg}>{actionData.error}</p>
                    )}
                    <Button type="submit" fluid size="large" className={formStyles.submitBtn}>
                        Set my password
                    </Button>
                </Form>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Sign up">
            <Form method="post" replace>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='user'
                        iconPosition='left'
                        placeholder='Username'
                        name='username'
                    />
                </div>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='lock'
                        iconPosition='left'
                        placeholder='Password'
                        name='password'
                        type='password'
                    />
                </div>
                <div className={formStyles.field}>
                    <Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder='Organisation code'
                        name='code'
                    />
                </div>
                {actionData?.error && (
                    <p className={formStyles.errorMsg}>{actionData.error}</p>
                )}
                <Button
                    type="submit"
                    fluid
                    size="large"
                    className={formStyles.submitBtn}
                >
                    Sign up
                </Button>
            </Form>
            <p className={formStyles.switchLink}>
                Already have an account? <a href={PATHS.LOGIN}>Log in</a>
            </p>
        </AuthCard>
    );
}

async function signupAction({ request }: LoaderFunctionArgs) {
    const formData = await request.formData();
    const inviteToken = formData.get('invite') as string | null;
    const password = formData.get('password') as string | null;

    if (!password || password.length < 10) {
        return { error: 'Password must be at least 10 characters.' };
    }

    // ---- Accepting an invitation -------------------------------------------------------------
    if (inviteToken) {
        try {
            const result = await client.mutate({
                mutation: ACCEPT_INVITATION,
                variables: { token: inviteToken, password },
            });

            if (!result?.data?.acceptInvitation?.success) {
                // The server answers identically for expired, revoked, used and unknown tokens, so
                // this message must not speculate about which it was.
                return { error: 'This invitation is no longer valid. Ask whoever invited you to send another.' };
            }

            await authProvider.signin(
                result.data.acceptInvitation.token,
                result.data.acceptInvitation.refreshToken,
            );
        } catch {
            return { error: 'Could not set your password. Please try again.' };
        }

        await client.resetStore();
        // Straight into the organisation they were invited to — their roles are already attached,
        // so there is nothing to onboard.
        return redirect(PATHS.ROOT);
    }

    // ---- Ordinary signup with an organisation code -------------------------------------------
    const code = formData.get('code') as string | null;
    const username = formData.get('username') as string | null;

    if (!username) {
        return { error: 'Please enter a username.' };
    }
    if (!code || code.length < 1) {
        return { error: 'You need an organisation code to sign up.' };
    }

    try {
        const result = await client.mutate({
            mutation: SIGNUP_MUTATION,
            variables: { userName: username, password, code },
        });
        if (result.errors) throw new Error();
        if (result?.data?.createUser?.token) {
            await authProvider.signin(result.data.createUser.token, result.data.createUser.refreshToken);
        }
    } catch {
        return { error: 'Invalid sign-up attempt. Check your organisation code and try again.' };
    }

    // See Login's loginAction — same reset is needed here so Layout's GET_ME guard
    // doesn't bounce the new user back to /organisations on their way to onboarding.
    await client.resetStore();

    return redirect(PATHS.ONBOARDING);
}

async function signupLoader({ request }: LoaderFunctionArgs) {
    const token = new URL(request.url).searchParams.get('invite');

    // An invitation link is followed by somebody who is, by definition, not signed in — but a
    // signed-in person clicking one would otherwise be bounced before the token was ever read.
    if (token) {
        try {
            const { data } = await client.query({
                query: INVITATION,
                variables: { token },
                fetchPolicy: 'network-only',
            });

            if (data?.invitation) {
                return { token, ...data.invitation };
            }
        } catch {
            // Fall through to the ordinary form.
        }

        // An unusable token lands on the normal signup page rather than an error screen. The
        // server will not say whether it expired, was revoked or never existed, and neither
        // should this.
        return null;
    }

    if (authProvider.isAuthenticated()) {
        return redirect(PATHS.ORGANISATIONS);
    }
    return null;
}

export { SignUpPage, signupAction, signupLoader };
